/**
 * WHAT A CASE FILE'S QUOTE SAYS ABOUT ITS HOURS.
 *
 * Anthony, 2026-10-16, for NYIA after Laura Dooley spent an hour on her
 * first quote: "240 minimum and make the work section easier to
 * understand and do for them. Add the ability to add additional
 * information option on the quote. Also, if rate is 240 tell on quote is
 * for one investigator. if it is 280 say it is for two investigators. Add
 * a note on all quotes that before we go past the agreed upon time we
 * will contact them if the case needs more time."
 *
 * The two rates are NYIA's own, said on thirty-four recorded calls (see
 * surveillance.ts): $240 an hour for one investigator, $280 for two. The
 * head count is read off the rate rather than stored, because the rate is
 * the only thing on a line and the two are one-to-one at this firm.
 *
 * Pure, like every other domain module: the editor, the quote email and
 * the client page all read the same sentences from here.
 */

/** No hour on a case file is sold under this. */
export const CASE_MIN_RATE = 240;
/** The two-investigator hour. */
export const CASE_TWO_RATE = 280;
/** What a field day is booked in, and what a new line starts at. */
export const CASE_MIN_HOURS = 5;

/** The per-day vehicle fee NYIA quotes on the phone. */
export const CASE_VEHICLE_FEE = 100;

/** The rate a case-file hour may carry: never under the minimum. */
export function caseRate(price: number): number {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return n;
  return n < CASE_MIN_RATE ? CASE_MIN_RATE : n;
}

/**
 * Who the hour pays for, read off its rate. Null for any other figure:
 * a flat service like a background check is not an investigator-hour,
 * and naming a head count on it would be a claim nobody made.
 */
export function investigatorsFor(rate: number): "one investigator" | "two investigators" | null {
  const n = Math.round(Number(rate) * 100) / 100;
  if (n === CASE_MIN_RATE) return "one investigator";
  if (n === CASE_TWO_RATE) return "two investigators";
  return null;
}

/**
 * A line as the CLIENT reads it. An hourly line at $240 or $280 says who
 * it pays for and how the number was reached; anything else reads as its
 * own description.
 */
export function caseLineText(l: { kind?: string | null; description?: string | null;
  quantity?: number | string | null; unit_price?: number | string | null }): string {
  const desc = String(l.description ?? "").trim();
  if (l.kind !== "labour" && l.kind !== "labor") return desc;
  const who = investigatorsFor(Number(l.unit_price));
  if (!who) return desc;
  const h = Number(l.quantity ?? 0);
  const hours = `${h} ${h === 1 ? "hour" : "hours"}`;
  return `${desc}: ${hours} at $${Number(l.unit_price).toFixed(0)} an hour, for ${who}`;
}

/** On every case-file quote, wherever it is read. */
export const MORE_TIME_NOTE =
  "Before we go past the agreed upon time, we will contact you if your case needs more time.";

/**
 * Service first, then expenses, then fees, then any discount. Laura,
 * 2026-10-16: "have the service listed before the fee". Stable inside
 * each kind, so the desk's own order holds.
 */
export function caseOrder<T extends { kind?: unknown }>(lines: T[]): T[] {
  const rank = (k: unknown) => (k === "labour" || k === "labor" ? 0 : k === "part" ? 1 : k === "discount" ? 3 : 2);
  return lines.map((l, i) => ({ l, i })).sort((a, b) => rank(a.l.kind) - rank(b.l.kind) || a.i - b.i).map((x) => x.l);
}
